import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Skull, CheckCircle, XCircle, Lightbulb } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const MythsVsFactsPage = () => {
  const navigate = useNavigate();
  const [revealedMyths, setRevealedMyths] = useState({});

  const myths = [
    {
      id: 1,
      myth: '"Ice in the ass wakes people up from GHB"',
      reality: 'GHB wears off fast on its own. Ice doesn\'t reverse G — it just shocks the body for a moment and can hurt someone.',
      tip: 'Ice ≠ antidote. Side position. Watch breathing. Call for help.',
      danger: true
    },
    {
      id: 2,
      myth: '"Naloxone works for everything"',
      reality: 'Naloxone ONLY works for opioids (fentanyl, heroin, oxy). It does NOT work for GHB, crystal meth, MDMA, coke, or ketamine.',
      tip: 'Carry naloxone, but don\'t expect it to fix G or Tina.',
      danger: true
    },
    {
      id: 3,
      myth: '"G is safe because it\'s common in PnP"',
      reality: 'GHB/GBL has a tiny line between high and overdose. Mixing with alcohol or benzos = very high risk.',
      tip: 'G is popular, not safe. Measure doses. Set timers. Don\'t mix.',
      danger: true
    },
    {
      id: 4,
      myth: '"Let them sleep it off"',
      reality: 'People can die while "sleeping it off" from choking or slow breathing.',
      tip: 'Don\'t leave people passed out alone. Side position. Check breathing. Call for help.',
      danger: true
    },
    {
      id: 5,
      myth: '"Mixing drugs is fine, it\'s part of PnP"',
      reality: 'Mixing = biggest overdose risk. G + alcohol/benzos = breathing can stop. Tina + Viagra + poppers = heart strain.',
      tip: 'Mixing = higher risk, not smarter play.',
      danger: true
    },
    {
      id: 6,
      myth: '"If you don\'t feel it, take more"',
      reality: 'Some drugs hit late. Redosing early is how people overdose.',
      tip: 'Wait it out. Give G/ketamine time before redosing (at least 45 min).',
      danger: true
    },
    {
      id: 7,
      myth: '"If they\'re still talking and walking, they\'re fine"',
      reality: 'People can look okay and still be overdosing. With G/downers, someone can crash suddenly.',
      tip: 'Looks okay ≠ okay. Check in. Watch breathing. Act early.',
      danger: true
    },
    {
      id: 8,
      myth: '"I know my body"',
      reality: 'Your body changes. Tolerance today isn\'t safe tomorrow.',
      tip: 'Listen to new red flags. Longer comedowns, anxiety, mood swings = slow down.',
      danger: false
    },
    {
      id: 9,
      myth: '"I know my dose, I don\'t need to measure"',
      reality: 'Different batches = different strength. Tolerance changes day to day.',
      tip: 'Measure your dose. Start low. Go slow.',
      danger: false
    },
    {
      id: 10,
      myth: '"Consent is automatic in party spaces"',
      reality: 'Being high changes power, memory, and ability to say no.',
      tip: 'Consent still matters when high. Check in. Respect no. Respect changing minds.',
      danger: false
    },
    {
      id: 11,
      myth: '"Everyone else can handle more than me"',
      reality: 'Bodies are different. Tolerance changes with sleep, food, meds, stress, HIV meds, PrEP.',
      tip: 'Your limits are valid. There\'s no prize for doing more.',
      danger: false
    },
    {
      id: 12,
      myth: '"I don\'t slam, so I\'m safe"',
      reality: 'All routes have risks. Smoking, snorting, booty bumping, oral = different harm, same danger.',
      tip: 'Clean tools + no sharing. Test your stuff. Know your route\'s risks.',
      danger: false
    },
    {
      id: 13,
      myth: '"Someone\'s always watching out for me"',
      reality: 'When everyone\'s high, no one\'s fully in control.',
      tip: 'Be your own safety plan. Test your own supply. Share location. Have a semi-sober check-in.',
      danger: false
    },
    {
      id: 14,
      myth: '"If you\'re not hooked yet, you\'re in control"',
      reality: 'Dependence builds quietly. The line between choice and compulsion blurs fast.',
      tip: 'Check your patterns. Breaking your own rules = time to pause and reflect.',
      danger: false
    },
    {
      id: 15,
      myth: '"Crashes just happen, you can\'t avoid them"',
      reality: 'Crashes are predictable. Prep reduces how hard they hit.',
      tip: 'Plan your comedown. Eat before. Hydrate during. Rest after. Low mood = chemistry, not weakness.',
      danger: false
    },
    {
      id: 16,
      myth: '"Harm reduction is only for hardcore users"',
      reality: 'If you use at all, harm reduction is for you.',
      tip: 'Basics save lives. Carry naloxone. Use your own tools. Bring water + condoms.',
      danger: false
    }
  ];

  const toggleReveal = (id) => {
    setRevealedMyths(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              data-testid="back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center space-x-2">
              <span className="text-3xl emoji-bounce">💀</span>
              <h1 className="text-2xl font-bold text-gradient">
                Myths vs Facts
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        {/* Hero */}
        <div className="mb-8 p-6 bg-gradient-to-br from-red-500 to-orange-500 rounded-3xl text-white text-center">
          <div className="text-5xl mb-3">⚠️</div>
          <h2 className="text-2xl font-bold mb-3">Party Myths Kill. Real Info Saves Lives.</h2>
          <p className="text-white/90 leading-relaxed">
            Tap each myth to reveal the truth. What you don't know CAN hurt you. 💀
          </p>
        </div>

        {/* Myths List */}
        <div className="space-y-4">
          {myths.map((item) => (
            <button
              key={item.id}
              onClick={() => toggleReveal(item.id)}
              data-testid={`myth-${item.id}`}
              className={`w-full text-left rounded-2xl overflow-hidden transition-all duration-300 ${
                item.danger ? 'border-2 border-[rgb(var(--danger))]/50' : 'border border-white/10'
              }`}
            >
              {/* Myth */}
              <div className={`p-5 ${revealedMyths[item.id] ? 'bg-[rgb(var(--danger))]/20' : 'bg-[rgb(var(--surface))]'}`}>
                <div className="flex items-start">
                  <XCircle 
                    size={24} 
                    weight="duotone" 
                    className={`mr-3 mt-0.5 flex-shrink-0 ${revealedMyths[item.id] ? 'text-[rgb(var(--danger))]' : 'text-[rgb(var(--text-secondary))]'}`} 
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase text-[rgb(var(--danger))] mb-1">
                        {item.danger ? '⚠️ DANGEROUS MYTH' : 'MYTH'}
                      </span>
                      <span className="text-xs text-[rgb(var(--text-secondary))]">
                        {revealedMyths[item.id] ? 'Tap to hide' : 'Tap for truth'}
                      </span>
                    </div>
                    <p className="text-[rgb(var(--text-primary))] font-semibold">
                      {item.myth}
                    </p>
                  </div>
                </div>
              </div>

              {/* Reality (revealed) */}
              {revealedMyths[item.id] && (
                <div className="bg-[rgb(var(--surface))] p-5 border-t border-white/10 animate-in fade-in slide-in-from-top-2">
                  <div className="flex items-start mb-3">
                    <CheckCircle size={20} weight="duotone" className="text-[rgb(var(--success))] mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="text-xs font-bold uppercase text-[rgb(var(--success))] mb-1 block">REALITY</span>
                      <p className="text-[rgb(var(--text-secondary))] text-sm leading-relaxed">
                        {item.reality}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start bg-[rgb(var(--primary))]/10 rounded-xl p-3">
                    <Lightbulb size={18} weight="duotone" className="text-[rgb(var(--primary))] mr-2 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-[rgb(var(--text-primary))] font-medium">
                      <strong>PSP Tip:</strong> {item.tip}
                    </p>
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-8 p-6 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 rounded-2xl border border-[rgb(var(--primary))]/20 text-center">
          <p className="text-[rgb(var(--text-primary))] font-bold mb-2">
            Knowledge saves lives.
          </p>
          <p className="text-sm text-[rgb(var(--text-secondary))]">
            Share this with someone who parties. Real info is harm reduction. 💜
          </p>
        </div>
      </main>
    </div>
  );
};

export default MythsVsFactsPage;
